import React, { useRef, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import { ArrowIcon } from '../../common/icons/arrow-icon'
import useOnClickOutside from '../../common/hooks/useOnClickOutside'
import { currentCompany, Company } from '../current-company'
import { theme } from '../theme'
import { Drawer } from '../drawer'
import { useLocalizationContext, AVAILABLE_LANGUAGES_LOCAL } from '../../common/components/localization-provider'

const Wrapper = styled.div<{ isOpen: boolean; dark: boolean }>`
    color: ${({ isOpen, dark, theme }) => {
        if (!isOpen && !dark) return theme.font.darkestColor
        if (dark && isOpen) return theme.font.darkestColor
        return theme.font.lightestColor
    }};
    font-family: ${({ theme }) => theme.font.extendedName};
`
const SelectText = styled.div`
    font-size: 16px;
    padding-top: 4px;
`
const SelectButton = styled.div<{ rotation: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    position: absolute;
    z-index: 9000;
    right: 46px;
    top: 51px;

    svg {
        transition: transform 0.2s;
        transform: rotate(${(p) => (p.rotation ? '180deg' : '0deg')});
    }

    &:hover {
        cursor: pointer;
    }

    @media (max-width: 576px) {
        top: 31px;
        right: 10px;
    }
`

const LanguageWrapper = styled.div<{ fortum: boolean }>`
    font-size: 16px;
    transition: all 0.2s ease;
    text-transform: capitalize;

    &:hover {
        cursor: pointer;
        color: ${({ fortum, theme }) => (fortum ? theme.fortumTheme.defaultColor : theme.font.successColor)};
    }
`

export type LanguageSelectDrawerProps = {
    className?: ClassName
    style?: React.CSSProperties
    dark?: boolean
}

export function LanguageSelectDrawer({
    className,
    style,
    dark = false,
}: LanguageSelectDrawerProps): React.ReactElement {
    const { locale, setLocale } = useLocalizationContext()
    const isFortumDomain = currentCompany === Company.FORTUM
    const [openDrawer, setOpenDrawer] = useState<boolean>(false)
    const drawerRef = useRef<HTMLDivElement>(null)
    useOnClickOutside(drawerRef, () => setOpenDrawer(false))

    const changeLanguage = (l: string) => {
        setLocale(l)

        setOpenDrawer(false)
    }

    return (
        <Wrapper className={cn(className)} style={style} ref={drawerRef} isOpen={openDrawer} dark={dark}>
            <SelectButton
                onClick={() => setOpenDrawer(!openDrawer)}
                rotation={!openDrawer}
                data-testid="languageSelector"
            >
                <SelectText>
                    <div>{locale.toUpperCase()}</div>
                </SelectText>

                <ArrowIcon color={isFortumDomain ? theme.fortumTheme.secondaryColor : theme.font.successColor} />
            </SelectButton>
            <Drawer className={cn({ open: openDrawer })} dark={dark}>
                {Object.keys(AVAILABLE_LANGUAGES_LOCAL)
                    .filter((l) => l !== locale)
                    .map((l, i) => {
                        const lang = AVAILABLE_LANGUAGES_LOCAL[l] ?? l
                        return (
                            <LanguageWrapper
                                key={i}
                                onClick={() => changeLanguage(l)}
                                data-testid={l}
                                fortum={isFortumDomain}
                            >
                                {lang}
                            </LanguageWrapper>
                        )
                    })}
            </Drawer>
        </Wrapper>
    )
}
